import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useActionHook } from "hooks";
import { IApiServiceError, usePortfolioConstructionActions } from "store";
import { ActiveSliceType, AumOverrideResponse, SliceGroup, SliceInvestor } from "store/types";
import { toFiniteNumber } from "utils/strings";

import { resolveDriftSlice } from "./sliceResolution";

const aumErrorFromApi = (e: unknown): string | null => {
  const fieldErrors = (e as IApiServiceError)?.errors;
  if (fieldErrors) {
    const match = Object.entries(fieldErrors).find(([field]) => field.includes("override") || field.includes("aum"));
    if (match) return Array.isArray(match[1]) ? match[1].join(" ") : String(match[1]);
  }
  return (e as IApiServiceError)?.message ?? null;
};

interface UseAumOverrideParams {
  activeSliceType: ActiveSliceType | null;
  activeSliceKey: string | null;
  selectedInvestorId: SliceInvestor["id"] | null;
  selectedGroupId: SliceGroup["id"] | null;
  onAumChanged?: () => void;
}

export const useAumOverride = ({
  activeSliceType,
  activeSliceKey,
  selectedInvestorId,
  selectedGroupId,
  onAumChanged
}: UseAumOverrideParams) => {
  const { getAumOverrideAction, setAumOverrideAction, resetAumOverrideAction } = usePortfolioConstructionActions();
  const [aumError, setAumError] = useState<string | null>(null);

  const fetchAum = useCallback(async (): Promise<AumOverrideResponse | null> => {
    const slice = resolveDriftSlice(activeSliceType, selectedInvestorId, selectedGroupId);
    if (!slice) return null;
    try {
      return await getAumOverrideAction(slice.sliceId, slice.sliceType);
    } catch {
      return null;
    }
  }, [activeSliceType, selectedInvestorId, selectedGroupId, getAumOverrideAction]);

  const { data: aumData = null, action: loadAum, setData: setAumData } = useActionHook(fetchAum);

  const aum = useMemo(() => {
    if (!aumData) return null;
    return aumData.override_value != null
      ? toFiniteNumber(aumData.override_value)
      : toFiniteNumber(aumData.calculated_nav_aum);
  }, [aumData]);
  const aumHasOverride = useMemo(() => aumData?.override_value != null, [aumData]);

  const updateAum = useCallback(
    async (value: number) => {
      const slice = resolveDriftSlice(activeSliceType, selectedInvestorId, selectedGroupId);
      if (!slice) return;
      try {
        const res = await setAumOverrideAction(slice.sliceId, slice.sliceType, value.toFixed(2));
        setAumData(res);
        setAumError(null);
        onAumChanged?.();
      } catch (e) {
        setAumError(aumErrorFromApi(e) ?? "Could not save AUM. Try again.");
      }
    },
    [activeSliceType, selectedInvestorId, selectedGroupId, setAumOverrideAction, setAumData, onAumChanged]
  );

  const resetAum = useCallback(async () => {
    const slice = resolveDriftSlice(activeSliceType, selectedInvestorId, selectedGroupId);
    if (!slice) return;
    try {
      const res = await resetAumOverrideAction(slice.sliceId, slice.sliceType);
      setAumData(res);
      setAumError(null);
      onAumChanged?.();
      toast.success("AUM override reset.");
    } catch (e) {
      setAumError(aumErrorFromApi(e) ?? "Could not reset AUM. Try again.");
    }
  }, [activeSliceType, selectedInvestorId, selectedGroupId, resetAumOverrideAction, setAumData, onAumChanged]);

  const clearAumError = useCallback(() => setAumError(null), []);

  useEffect(() => {
    loadAum();
    setAumError(null);
  }, [activeSliceKey, loadAum]);

  return { aum, aumHasOverride, updateAum, resetAum, aumError, clearAumError };
};
